import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import { getConfig } from "lib/get-config";
import { usePaths } from "lib/navigation/use-paths";
import { useDebouncedCallback } from "lib/utils/use-debounce";

import { getAutoBookmark, postAutoBookmark } from "./get-bookmarks";

/**
 * Hook to read the initial xpointer from auto-bookmark
 * Only for determining initial position - doesn't require content
 */
export function useInitialXpointer() {
  const { art, user } = getConfig();

  const { data: autoBookmark } = useQuery({
    queryKey: ["autobookmark", art],
    queryFn: () => getAutoBookmark(art),
    enabled: !!user,
  });

  return {
    autoBookmark,
  };
}

/**
 * Hook to create/update auto-bookmarks with chapter titles
 * Requires being inside InitialContentProvider
 */
export function useAutoBookmark() {
  const { art, user } = getConfig();
  const { xpointerToChapter } = usePaths();

  const { data: autoBookmark } = useQuery({
    queryKey: ["autobookmark", art],
    queryFn: () => getAutoBookmark(art),
    enabled: !!user,
  });
  const currentVersion = useRef<number | null>(
    autoBookmark?.data?.version ?? null
  );
  // Цепочка промисов — каждый новый запрос ждёт завершения предыдущего
  const requestChainRef = useRef<Promise<void>>(Promise.resolve());

  function createBookmark(xpointer: string, percent: number) {
    requestChainRef.current = requestChainRef.current.then(async () => {
      currentVersion.current = (currentVersion.current ?? -1) + 1;
      const newAutoBookmark = {
        id: uuid(),
        created_at: new Date().toISOString().split(".")[0],
        percent: percent,
        version: currentVersion.current,
        xpointer: xpointer,
        is_epub: false,
        device_name: "web",
        server_time: new Date().toISOString(),
        chapter_title: xpointerToChapter(xpointer)?.t || "",
      };

      const res = await postAutoBookmark(art, newAutoBookmark);

      if (res.status === 409 && res.conflictVersion != null) {
        // 409: синхронизируем версию
        const retryVersion = res.conflictVersion + 1;
        currentVersion.current = retryVersion;
        await postAutoBookmark(art, {
          ...newAutoBookmark,
          version: retryVersion,
        });
      } else if (!res.response?.ok) {
        // Ошибка: откат версии
        currentVersion.current = (currentVersion.current ?? 1) - 1;
      }
    });
  }

  const createAutoBookmark = useDebouncedCallback(createBookmark, 600);

  return {
    autoBookmark,
    createAutoBookmark,
  };
}
